/**
 * Admin Bugs Page — list, filter, and resolve bug reports.
 * Route: /admin/bugs (admin/manager only — server enforces).
 */
import { navigate } from '../../router';

interface BugRow {
  id: number;
  user_id: number | null;
  lesson_id: string | null;
  step_id: string | null;
  type: 'auto' | 'user_report';
  message: string;
  stack: string | null;
  user_agent: string | null;
  url: string | null;
  context_json: any;
  occurrence_count: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
}

interface Filters {
  resolved: '' | '0' | '1';
  type: '' | 'auto' | 'user_report';
  lessonId: string;
}

const filters: Filters = { resolved: '0', type: '', lessonId: '' };
let pageContainer: HTMLElement | null = null;

async function fetchBugs(): Promise<{ ok: boolean; status: number; bugs: BugRow[] }> {
  const params = new URLSearchParams();
  if (filters.resolved) params.set('resolved', filters.resolved);
  if (filters.type) params.set('type', filters.type);
  if (filters.lessonId) params.set('lessonId', filters.lessonId);

  const res = await fetch(`/api/v1/bugs/admin?${params.toString()}`, { credentials: 'include' });
  if (!res.ok) return { ok: false, status: res.status, bugs: [] };
  const json = await res.json();
  return { ok: true, status: 200, bugs: json.data?.bugs ?? [] };
}

async function setResolved(id: number, resolved: boolean): Promise<void> {
  await fetch(`/api/v1/bugs/admin/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ resolved }),
  });
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function rowHtml(bug: BugRow): string {
  const typeBadge =
    bug.type === 'user_report'
      ? '<span class="bug-badge bug-badge-user">User</span>'
      : '<span class="bug-badge bug-badge-auto">Auto</span>';
  const status = bug.resolved_at
    ? '<span class="bug-status bug-status-resolved">Resolved</span>'
    : '<span class="bug-status bug-status-open">Open</span>';
  const lesson = bug.lesson_id ? `${escapeHtml(bug.lesson_id)}${bug.step_id ? ' · ' + escapeHtml(bug.step_id) : ''}` : '—';
  const count = bug.occurrence_count > 1 ? `<span class="bug-count">×${bug.occurrence_count}</span>` : '';
  const messageShort = escapeHtml(bug.message.slice(0, 120));

  return `
    <tr class="bug-row" data-bug-id="${bug.id}">
      <td>${fmtDate(bug.last_seen_at)}</td>
      <td>${typeBadge}</td>
      <td>${lesson}</td>
      <td>${bug.user_id ?? '—'}</td>
      <td class="bug-msg">${messageShort} ${count}</td>
      <td>${status}</td>
      <td>
        <button class="bug-expand" data-bug-id="${bug.id}">Details</button>
        <button class="bug-toggle" data-bug-id="${bug.id}" data-resolved="${bug.resolved_at ? '1' : '0'}">
          ${bug.resolved_at ? 'Reopen' : 'Resolve'}
        </button>
      </td>
    </tr>
    <tr class="bug-detail" id="bug-detail-${bug.id}" style="display:none;">
      <td colspan="7">
        <div class="bug-detail-inner">
          <div><strong>Message:</strong> ${escapeHtml(bug.message)}</div>
          ${bug.stack ? `<div><strong>Stack:</strong><pre>${escapeHtml(bug.stack)}</pre></div>` : ''}
          ${bug.url ? `<div><strong>URL:</strong> ${escapeHtml(bug.url)}</div>` : ''}
          ${bug.user_agent ? `<div><strong>UA:</strong> ${escapeHtml(bug.user_agent)}</div>` : ''}
          ${bug.context_json ? `<div><strong>Context:</strong> <pre>${escapeHtml(JSON.stringify(bug.context_json, null, 2))}</pre></div>` : ''}
          <div class="bug-detail-meta">First seen ${fmtDate(bug.first_seen_at)} · ID ${bug.id}</div>
        </div>
      </td>
    </tr>
  `;
}

async function render(container: HTMLElement): Promise<void> {
  pageContainer = container;
  container.innerHTML = `
    <div class="admin-bugs-page">
      <header class="admin-bugs-header">
        <button class="admin-bugs-back">&larr; Back</button>
        <h1>Bug Reports</h1>
      </header>
      <div class="admin-bugs-filters">
        <label>Status
          <select data-filter="resolved">
            <option value="0" ${filters.resolved === '0' ? 'selected' : ''}>Open</option>
            <option value="1" ${filters.resolved === '1' ? 'selected' : ''}>Resolved</option>
            <option value="" ${filters.resolved === '' ? 'selected' : ''}>All</option>
          </select>
        </label>
        <label>Type
          <select data-filter="type">
            <option value="" ${filters.type === '' ? 'selected' : ''}>All</option>
            <option value="auto" ${filters.type === 'auto' ? 'selected' : ''}>Auto</option>
            <option value="user_report" ${filters.type === 'user_report' ? 'selected' : ''}>User</option>
          </select>
        </label>
        <label>Lesson
          <input data-filter="lessonId" type="text" placeholder="e.g. ent-gfx-02" value="${escapeHtml(filters.lessonId)}" />
        </label>
        <button class="admin-bugs-refresh">Refresh</button>
      </div>
      <div class="admin-bugs-body">Loading…</div>
    </div>
  `;

  container.querySelector('.admin-bugs-back')?.addEventListener('click', () => navigate('/'));
  container.querySelector('.admin-bugs-refresh')?.addEventListener('click', reload);

  container.querySelectorAll<HTMLSelectElement | HTMLInputElement>('[data-filter]').forEach((el) => {
    const key = el.dataset.filter as keyof Filters;
    el.addEventListener('change', () => {
      (filters as any)[key] = el.value;
      reload();
    });
  });

  await reload();
}

async function reload(): Promise<void> {
  if (!pageContainer) return;
  const body = pageContainer.querySelector<HTMLElement>('.admin-bugs-body');
  if (!body) return;
  body.textContent = 'Loading…';

  const result = await fetchBugs();
  if (!result.ok) {
    body.innerHTML =
      result.status === 401 || result.status === 403
        ? '<div class="admin-bugs-empty">Admin or manager role required.</div>'
        : '<div class="admin-bugs-empty">Failed to load reports.</div>';
    return;
  }

  if (result.bugs.length === 0) {
    body.innerHTML = '<div class="admin-bugs-empty">No reports match the current filters.</div>';
    return;
  }

  body.innerHTML = `
    <table class="admin-bugs-table">
      <thead>
        <tr>
          <th>Last Seen</th>
          <th>Type</th>
          <th>Lesson · Step</th>
          <th>User</th>
          <th>Message</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>${result.bugs.map(rowHtml).join('')}</tbody>
    </table>
  `;

  body.querySelectorAll<HTMLButtonElement>('.bug-expand').forEach((btn) => {
    btn.addEventListener('click', () => {
      const row = body.querySelector<HTMLElement>(`#bug-detail-${btn.dataset.bugId}`);
      if (row) row.style.display = row.style.display === 'none' ? '' : 'none';
    });
  });

  body.querySelectorAll<HTMLButtonElement>('.bug-toggle').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = parseInt(btn.dataset.bugId ?? '0', 10);
      const currentlyResolved = btn.dataset.resolved === '1';
      btn.disabled = true;
      await setResolved(id, !currentlyResolved);
      reload();
    });
  });
}

export function renderAdminBugs(container: HTMLElement): void {
  void render(container);
}
